import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import Navbar from './components/Navbar';
import Homepage from './components/Homepage';
import Onboarding from './components/Onboarding';
import Login from './components/Login';
import SignUpPage from './components/SignUpPage';
import Dashboard from './components/Dashboard';
import Profile from './components/Profile';
import ThemeToggle from './components/ThemeToggle';
import AboutUs from './components/AboutUs';
import Help from './components/Help';
import AdminLayout from './components/AdminLayout';
import AdminDashboard from './components/AdminDashboard';
import ExerciseEditor from './components/ExerciseEditor';
import ExerciseManagement from './components/ExerciseManagement';
import UsersManagement from './components/UsersManagement';
import UnitsManagement from './components/UnitsManagement';
import ExerciseBrowser from './components/ExerciseBrowser';
import ExerciseSolver from './components/ExerciseSolver';
import LessonEditor from './components/LessonEditor';
import LessonBrowser from './components/LessonBrowser';
import LessonViewer from './components/LessonViewer';
import UnitsPage from './components/UnitsPage';
import './App.css';
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (!loading && !user) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

const FirstTimeRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, loading } = useAuth();

  useEffect(() => {
    if (loading) return;
    
    // Don't redirect if user is on auth pages
    const authPages = ['/login', '/signup', '/onboarding'];
    if (authPages.some(page => location.pathname.startsWith(page))) {
      return;
    }

    // Only redirect authenticated users without onboarding to onboarding page
    if (user && !profile?.onboarding_data && location.pathname === '/dashboard') {
      console.log('User missing onboarding data, redirecting');
      navigate('/onboarding', { replace: true });
    }
  }, [loading, location.pathname, navigate, profile, user]);

  return null;
};

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <ThemeProvider>
          <Router>
            <FirstTimeRedirect />
            <div className="App">
              <Navbar />
              <Routes>
                <Route path="/" element={<Homepage />} />
                <Route path="/onboarding" element={<Onboarding />} />
                <Route path="/login/*" element={<Login />} />
                <Route path="/signup/*" element={<SignUpPage />} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  }
                />
                <Route path="/about" element={<AboutUs />} />
                <Route path="/help" element={<Help />} />
                <Route
                  path="/practice"
                  element={
                    <ProtectedRoute>
                      <ExerciseBrowser />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/practice/exercise/:id"
                  element={
                    <ProtectedRoute>
                      <ExerciseSolver />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/learn"
                  element={
                    <ProtectedRoute>
                      <LessonBrowser />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/learn/lesson/:id"
                  element={
                    <ProtectedRoute>
                      <LessonViewer />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/units"
                  element={
                    <ProtectedRoute>
                      <UnitsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<AdminDashboard />} />
                  <Route path="exercises" element={<ExerciseManagement />} />
                  <Route path="exercises/new" element={<ExerciseEditor />} />
                  <Route path="exercises/:id/edit" element={<ExerciseEditor />} />
                  <Route path="units" element={<UnitsManagement />} />
                  <Route path="lessons/new" element={<LessonEditor />} />
                  <Route path="lessons/:id/edit" element={<LessonEditor />} />
                  <Route path="users" element={<UsersManagement />} />
                </Route>
              </Routes>
              <ThemeToggle />
            </div>
          </Router>
        </ThemeProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
